import React, { useState, useEffect, useCallback } from 'react';
import {
  Kanban, Lock, Plus, X, Pencil, Trash2, GripVertical, Thermometer,
  Tag as TagIcon, MessageCircle, IdCard, Mail, MapPin
} from 'lucide-react';
import apiClient from '../../utils/apiClient';
import { formatEcuadorDate } from '../../utils/ecuadorTime';
import CustomAlert from './shared/CustomAlert';

// ⭐ Pipeline como pantalla propia del dashboard (pedido de Angelo,
// 2026-08-05: "no que esté dentro de conversaciones" -- antes vivía como
// un toggle Chats/Pipeline dentro de Conversaciones.js; ahora es su propio
// tab en el sidebar). Reutiliza el mismo backend (crm_pipeline_stages +
// GET /api/pos/crm/contacts) y el mismo gate de acceso que Conversaciones
// (hasCrmAccess), porque sigue siendo parte del mismo addon CRM -- no se
// vende por separado.

const TEMPERATURE_OPTIONS = [
  { value: 'frio', label: 'Frío', color: 'bg-blue-100 text-blue-700 border-blue-300' },
  { value: 'tibio', label: 'Tibio', color: 'bg-orange-100 text-orange-700 border-orange-300' },
  { value: 'caliente', label: 'Caliente', color: 'bg-red-100 text-red-700 border-red-300' },
];

const STAGE_COLOR_SWATCHES = ['#3b82f6', '#8b5cf6', '#f59e0b', '#f97316', '#22c55e', '#ef4444', '#ec4899', '#6b7280'];

// ⭐ Tarjeta de contacto del tablero. `draggable` + los 3 handlers de abajo
// son drag-and-drop nativo de HTML5 (sin librería nueva -- el resto del
// proyecto no usa ninguna, y sumar una solo para esto no se justifica).
// Limitación conocida y aceptada: el DnD nativo del navegador no funciona
// bien con touch (tablets) -- por eso el <select> de abajo NO es
// decorativo, es el camino que sí funciona en touch/accesibilidad;
// arrastrar es un atajo encima de eso, no la única forma de mover una
// tarjeta.
// ⭐ Color de etapa aplicado a la tarjeta (pedido de Angelo, 2026-08-07:
// "cuando coloque un color en una etapa, los contactos que estén ahí se
// coloquen ese color"). Antes la tarjeta era blanca sin importar la etapa
// -- el color solo se veía en el punto del header de la columna. Ahora se
// pinta un borde izquierdo grueso con `stageColor` (el color de la
// columna a la que pertenece esta tarjeta) para que el color "viaje" con
// el contacto, no solo con la columna -- útil también en vistas futuras
// donde los contactos de distintas etapas se mezclen (ver Conversaciones.js).
const PipelineCard = ({ contact, stages, stageColor, onDragStart, onDragEnd, onMove, onOpen }) => {
  const tempOpt = TEMPERATURE_OPTIONS.find(t => t.value === contact.temperature);
  return (
    <div
      draggable
      onDragStart={() => onDragStart(contact.id)}
      onDragEnd={onDragEnd}
      onClick={() => onOpen(contact)}
      style={{ borderLeft: `4px solid ${stageColor || '#e5e7eb'}` }}
      className="bg-white border rounded-lg p-2.5 shadow-sm hover:shadow-md cursor-pointer transition group"
    >
      <div className="flex items-start justify-between gap-1">
        <p className="text-sm font-semibold text-gray-800 truncate">{contact.name || contact.phone || 'Sin nombre'}</p>
        <GripVertical className="w-3.5 h-3.5 text-gray-300 flex-shrink-0 opacity-0 group-hover:opacity-100" />
      </div>
      <p className="text-xs text-gray-500 truncate">{contact.phone}</p>
      <div className="flex items-center justify-between gap-1 mt-1.5">
        {tempOpt ? (
          <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-semibold border ${tempOpt.color}`}>{tempOpt.label}</span>
        ) : <span />}
        {contact.updated_at && <span className="text-[10px] text-gray-400">{formatEcuadorDate(contact.updated_at)}</span>}
      </div>
      {/* ⭐ Valor del prospecto. El título distingue si es heredado de la etapa
          o cargado a mano, para que no haya que adivinar de dónde sale. */}
      {Number(contact.effective_value || 0) > 0 && (
        <p
          className="text-[11px] font-bold text-emerald-600 mt-1"
          title={contact.opportunity_value != null ? 'Valor propio de este contacto' : 'Heredado del valor de la etapa'}
        >
          ${Number(contact.effective_value).toLocaleString('es-EC', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          {contact.opportunity_value == null && <span className="font-normal text-gray-400"> · etapa</span>}
        </p>
      )}
      <select
        value={contact.pipeline_stage_id || ''}
        onChange={(e) => onMove(contact.id, e.target.value ? Number(e.target.value) : null)}
        onClick={(e) => e.stopPropagation()}
        className="w-full mt-1.5 text-[10px] border rounded px-1 py-0.5 text-gray-500"
      >
        <option value="">Sin etapa</option>
        {stages.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
      </select>
    </div>
  );
};

// ⭐ Tablero: columnas = etapas del embudo (crm_pipeline_stages); tarjetas =
// contactos, agrupados por pipeline_stage_id. La columna "Sin etapa" no es
// una fila real de la tabla -- es todo contacto con pipeline_stage_id NULL,
// siempre primera y no se puede editar/borrar.
const PipelineBoard = ({
  stages, contacts, loading, draggedContactId, setDraggedContactId,
  onMoveContact, onOpenContact, onCreateStage, onUpdateStage, onDeleteStage, onReorderStage,
}) => {
  const [dragOverKey, setDragOverKey] = useState(null);
  const [addingStage, setAddingStage] = useState(false);
  const [newStageName, setNewStageName] = useState('');
  const [newStageColor, setNewStageColor] = useState(STAGE_COLOR_SWATCHES[0]);
  const [editingStageId, setEditingStageId] = useState(null);
  const [editStageName, setEditStageName] = useState('');
  const [editStageColor, setEditStageColor] = useState(STAGE_COLOR_SWATCHES[0]);
  // ⭐ Valor de oportunidad por etapa (2026-08-08). Es el valor POR DEFECTO:
  // cada contacto que cae en la etapa lo hereda, y se puede pisar contacto por
  // contacto. `isWon` marca las etapas de cierre ganado, que el tablero suma
  // aparte del pipeline abierto.
  const [editStageValue, setEditStageValue] = useState('0');
  const [editStageWon, setEditStageWon] = useState(false);
  const [newStageValue, setNewStageValue] = useState('0');
  const [newStageWon, setNewStageWon] = useState(false);

  const money = (n) => '$' + Number(n || 0).toLocaleString('es-EC', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  // El valor efectivo de un contacto: el suyo propio si lo tiene, si no el de
  // su etapa. `effective_value` ya viene calculado del backend; el fallback
  // cubre la lista optimista que arma el drag & drop antes de recargar.
  const contactValue = (c, stage) => (
    c.effective_value != null
      ? Number(c.effective_value)
      : (c.opportunity_value != null ? Number(c.opportunity_value) : Number(stage?.default_value || 0))
  );

  const columns = [{ id: null, key: 'none', name: 'Sin etapa', color: '#9ca3af', isNone: true }, ...stages.map(s => ({ ...s, key: String(s.id) }))];

  const startEditStage = (stage) => {
    setEditingStageId(stage.id);
    setEditStageName(stage.name);
    setEditStageColor(stage.color || STAGE_COLOR_SWATCHES[0]);
    setEditStageValue(String(stage.default_value ?? 0));
    setEditStageWon(!!stage.is_won);
  };

  const saveEditStage = () => {
    if (!editStageName.trim()) return;
    onUpdateStage(editingStageId, {
      name: editStageName.trim(),
      color: editStageColor,
      default_value: Number(editStageValue) || 0,
      is_won: editStageWon
    });
    setEditingStageId(null);
  };

  const createStage = () => {
    if (!newStageName.trim()) return;
    onCreateStage({
      name: newStageName.trim(),
      color: newStageColor,
      default_value: Number(newStageValue) || 0,
      is_won: newStageWon
    });
    setNewStageName('');
    setNewStageColor(STAGE_COLOR_SWATCHES[0]);
    setNewStageValue('0');
    setNewStageWon(false);
    setAddingStage(false);
  };

  return (
    <div className="flex-1 overflow-x-auto overflow-y-hidden bg-gray-50">
      <div className="flex gap-3 p-3 h-full items-start">
        {columns.map((col) => {
          const colContacts = contacts.filter(c => (col.isNone ? !c.pipeline_stage_id : c.pipeline_stage_id === col.id));
          return (
            <div
              key={col.key}
              onDragOver={(e) => { e.preventDefault(); setDragOverKey(col.key); }}
              onDragLeave={() => setDragOverKey(k => (k === col.key ? null : k))}
              onDrop={(e) => {
                e.preventDefault();
                setDragOverKey(null);
                if (draggedContactId != null) onMoveContact(draggedContactId, col.id);
                setDraggedContactId(null);
              }}
              className={`w-64 flex-shrink-0 flex flex-col rounded-lg border bg-gray-100/60 max-h-full ${dragOverKey === col.key ? 'ring-2 ring-blue-400' : ''}`}
            >
              <div className="p-2.5 border-b bg-white rounded-t-lg">
                {/* ⭐ Fix bug crítico (reporte Angelo 2026-08-07: columna "Sin
                    etapa" quedaba permanentemente en modo edición): el
                    sentinel de "no estoy editando nada" es `null`, pero la
                    columna virtual "Sin etapa" también tiene `id: null` --
                    cualquier vez que `editingStageId` vuelve a `null`
                    (guardar o cancelar una edición real, o simplemente al
                    montar el componente) la comparación `null === null` se
                    cumplía para esa columna y la dejaba mostrando el
                    formulario de edición en vez de su header normal, sin
                    forma de salir (Guardar mandaba un PATCH con id null,
                    Cancelar volvía a fijar null sobre null, ninguno hacía
                    nada visible). "Sin etapa" nunca debe poder entrar en
                    modo edición -- por eso ya tenía sus botones de
                    lápiz/basura ocultos con `!col.isNone` más abajo; ahora
                    el propio check de modo-edición respeta esa misma regla. */}
                {!col.isNone && editingStageId === col.id ? (
                  <div className="space-y-1.5">
                    <input
                      autoFocus
                      value={editStageName}
                      onChange={(e) => setEditStageName(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') saveEditStage(); if (e.key === 'Escape') setEditingStageId(null); }}
                      className="w-full px-1.5 py-1 border rounded text-xs"
                    />
                    <div className="flex gap-1">
                      {STAGE_COLOR_SWATCHES.map(c => (
                        <button key={c} onClick={() => setEditStageColor(c)} style={{ backgroundColor: c }} className={`w-4 h-4 rounded-full ${editStageColor === c ? 'ring-2 ring-offset-1 ring-gray-500' : ''}`} />
                      ))}
                    </div>
                    <div className="flex items-center gap-1">
                      <span className="text-[10px] text-gray-500 flex-shrink-0">Valor $</span>
                      <input
                        type="number" min="0" step="0.01"
                        value={editStageValue}
                        onChange={(e) => setEditStageValue(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter') saveEditStage(); }}
                        className="w-full px-1.5 py-1 border rounded text-xs"
                        title="Valor por defecto de cada prospecto que caiga en esta etapa. Se puede cambiar contacto por contacto."
                      />
                    </div>
                    <label className="flex items-center gap-1.5 text-[10px] text-gray-600 cursor-pointer">
                      <input type="checkbox" checked={editStageWon} onChange={(e) => setEditStageWon(e.target.checked)} />
                      <span>Es etapa de cierre ganado</span>
                    </label>
                    <div className="flex gap-1.5">
                      <button onClick={saveEditStage} className="flex-1 text-[10px] py-1 bg-blue-600 text-white rounded font-semibold">Guardar</button>
                      <button onClick={() => setEditingStageId(null)} className="flex-1 text-[10px] py-1 bg-gray-200 text-gray-600 rounded font-semibold">Cancelar</button>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center justify-between gap-1">
                    <div className="flex items-center gap-1.5 min-w-0">
                      <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: col.color }} />
                      <span className="text-sm font-semibold text-gray-800 truncate">{col.name}</span>
                      <span className="text-[10px] text-gray-400 flex-shrink-0">({colContacts.length})</span>
                    </div>
                    {/* ⭐ Total de la columna: la suma de lo que vale cada
                        prospecto que está parado ahí. Es el número que el dueño
                        mira para decidir dónde empujar. */}
                    {!col.isNone && colContacts.length > 0 && (
                      <span className="text-[10px] font-bold text-emerald-600 flex-shrink-0 mr-1">
                        {money(colContacts.reduce((sum, c) => sum + contactValue(c, col), 0))}
                      </span>
                    )}
                    {!col.isNone && (
                      <div className="flex items-center gap-0.5 flex-shrink-0">
                        <button onClick={() => onReorderStage(col.id, -1)} className="p-1 rounded text-gray-400 hover:text-blue-600 hover:bg-blue-50" title="Subir etapa">↑</button>
                        <button onClick={() => onReorderStage(col.id, 1)} className="p-1 rounded text-gray-400 hover:text-blue-600 hover:bg-blue-50" title="Bajar etapa">↓</button>
                        <button onClick={() => startEditStage(col)} className="p-1 rounded text-gray-400 hover:text-blue-600 hover:bg-blue-50"><Pencil className="w-3 h-3" /></button>
                        <button onClick={() => onDeleteStage(col)} className="p-1 rounded text-gray-400 hover:text-red-600 hover:bg-red-50"><Trash2 className="w-3 h-3" /></button>
                      </div>
                    )}
                  </div>
                )}
              </div>
              <div className="flex-1 overflow-y-auto p-2 space-y-2 min-h-[80px]">
                {colContacts.map(contact => (
                  <PipelineCard
                    key={contact.id}
                    contact={contact}
                    stages={stages}
                    stageColor={col.color}
                    onDragStart={setDraggedContactId}
                    onDragEnd={() => setDraggedContactId(null)}
                    onMove={onMoveContact}
                    onOpen={onOpenContact}
                  />
                ))}
                {colContacts.length === 0 && <p className="text-center text-[10px] text-gray-400 py-4">Sin contactos</p>}
              </div>
            </div>
          );
        })}

        <div className="w-56 flex-shrink-0">
          {addingStage ? (
            <div className="bg-white border rounded-lg p-2.5 space-y-1.5">
              <input
                autoFocus
                placeholder="Nombre de la etapa"
                value={newStageName}
                onChange={(e) => setNewStageName(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') createStage(); if (e.key === 'Escape') setAddingStage(false); }}
                className="w-full px-1.5 py-1 border rounded text-xs"
              />
              <div className="flex gap-1">
                {STAGE_COLOR_SWATCHES.map(c => (
                  <button key={c} onClick={() => setNewStageColor(c)} style={{ backgroundColor: c }} className={`w-4 h-4 rounded-full ${newStageColor === c ? 'ring-2 ring-offset-1 ring-gray-500' : ''}`} />
                ))}
              </div>
              <div className="flex items-center gap-1">
                <span className="text-[10px] text-gray-500 flex-shrink-0">Valor $</span>
                <input
                  type="number" min="0" step="0.01"
                  value={newStageValue}
                  onChange={(e) => setNewStageValue(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') createStage(); }}
                  className="w-full px-1.5 py-1 border rounded text-xs"
                  title="Valor por defecto de cada prospecto que caiga en esta etapa"
                />
              </div>
              <label className="flex items-center gap-1.5 text-[10px] text-gray-600 cursor-pointer">
                <input type="checkbox" checked={newStageWon} onChange={(e) => setNewStageWon(e.target.checked)} />
                <span>Es etapa de cierre ganado</span>
              </label>
              <div className="flex gap-1.5">
                <button onClick={createStage} className="flex-1 text-[10px] py-1 bg-blue-600 text-white rounded font-semibold">Crear</button>
                <button onClick={() => setAddingStage(false)} className="flex-1 text-[10px] py-1 bg-gray-200 text-gray-600 rounded font-semibold">Cancelar</button>
              </div>
            </div>
          ) : (
            <button
              onClick={() => setAddingStage(true)}
              className="w-full py-2.5 border-2 border-dashed rounded-lg text-xs font-semibold text-gray-400 hover:text-gray-600 hover:border-gray-400 flex items-center justify-center gap-1"
            >
              <Plus className="w-3.5 h-3.5" /> Nueva etapa
            </button>
          )}
        </div>
      </div>
      {loading && <p className="text-center text-xs text-gray-400 py-2">Actualizando...</p>}
    </div>
  );
};

const PipelineManager = ({ onOpenConversations }) => {
  const [loadingAccess, setLoadingAccess] = useState(true);
  const [hasAccess, setHasAccess] = useState(false);
  // ⭐ Múltiples pipelines (pedido de Angelo, 2026-08-05: "que pueda tener
  // varios pipelines totalmente editables, algo básico como GHL"). `stages`
  // ahora es SOLO las etapas del pipeline seleccionado (?pipeline_id= en el
  // GET) -- ver loadStagesAndTags más abajo.
  const [pipelines, setPipelines] = useState([]);
  const [selectedPipelineId, setSelectedPipelineId] = useState(null);
  const [editingPipelineId, setEditingPipelineId] = useState(null);
  const [pipelineNameDraft, setPipelineNameDraft] = useState('');
  const [addingPipeline, setAddingPipeline] = useState(false);
  const [newPipelineName, setNewPipelineName] = useState('');
  const [stages, setStages] = useState([]);
  const [tags, setTags] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [draggedContactId, setDraggedContactId] = useState(null);
  const [customAlert, setCustomAlert] = useState(null);

  // ⭐ Drawer de detalle: al hacer click en una tarjeta se pide el contacto
  // completo (GET .../contacts/:id, mismo endpoint que usaba Conversaciones)
  // -- la lista del tablero no trae notas/tags completos, solo lo necesario
  // para pintar la tarjeta.
  const [selectedContact, setSelectedContact] = useState(null);
  const [loadingContact, setLoadingContact] = useState(false);
  // ⭐ Mismo selector de etiquetas que Conversaciones.js (2026-08-07):
  // asignadas y disponibles-para-agregar en dos lugares separados, nunca
  // mezcladas en la misma fila -- ver el comentario largo en
  // Conversaciones.js sobre el reporte de Angelo "se agregó a todos".
  const [showTagPicker, setShowTagPicker] = useState(false);
  const [creatingTag, setCreatingTag] = useState(false);
  const [newTagName, setNewTagName] = useState('');
  const [newTagColor, setNewTagColor] = useState(STAGE_COLOR_SWATCHES[0]);
  const [noteInput, setNoteInput] = useState('');

  const showAlert = (type, title, message) => setCustomAlert({ type, title, message });
  // El interceptor global también avisa ante una respuesta 202, pero el
  // tablero se refresca enseguida después de editar una etapa y podía tapar
  // ese aviso. Aquí lo mostramos de forma explícita y no recargamos hasta
  // que MARKETOS apruebe y aplique el cambio.
  const showApprovalQueued = (response) => {
    if (!response?.data?.approval_required) return false;
    showAlert('success', 'Solicitud enviada a revisión', response.data.message || 'Recibimos tu solicitud. MARKETOS la revisará antes de aplicarla.');
    return true;
  };

  const loadAccess = useCallback(async () => {
    try {
      const res = await apiClient.get('/api/pos/crm/access');
      setHasAccess(Boolean(res.data?.hasCrmAccess));
    } catch (err) {
      setHasAccess(false);
    } finally {
      setLoadingAccess(false);
    }
  }, []);

  // ⭐ GET /pipelines auto-siembra el primer pipeline (y adopta etapas
  // viejas sin pipeline_id) la primera vez que se llama -- ver el
  // comentario completo en el backend. Acá solo se selecciona el primero
  // si todavía no hay ninguno elegido, sin pisar la elección del usuario
  // en recargas posteriores.
  const loadPipelines = useCallback(async () => {
    try {
      const res = await apiClient.get('/api/pos/crm/pipelines');
      const rows = res.data || [];
      setPipelines(rows);
      setSelectedPipelineId(prev => (prev && rows.some(p => p.id === prev)) ? prev : (rows[0]?.id ?? null));
    } catch (err) { /* silencioso */ }
  }, []);

  const loadStagesAndTags = useCallback(async (pipelineId) => {
    if (!pipelineId) { setStages([]); return; }
    try {
      const [stagesRes, tagsRes] = await Promise.all([
        apiClient.get('/api/pos/crm/pipeline-stages', { params: { pipeline_id: pipelineId } }),
        apiClient.get('/api/pos/crm/tags'),
      ]);
      setStages(stagesRes.data || []);
      setTags(tagsRes.data || []);
    } catch (err) { /* silencioso */ }
  }, []);

  const loadContacts = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiClient.get('/api/pos/crm/contacts');
      setContacts(res.data || []);
    } catch (err) { /* silencioso, se reintenta al refrescar */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadAccess(); }, [loadAccess]);

  useEffect(() => {
    if (!hasAccess) return;
    loadPipelines();
    loadContacts();
  }, [hasAccess, loadPipelines, loadContacts]);

  // ⭐ Fix (reporte Angelo 2026-08-07: "borro un contacto desde
  // Conversaciones y sigue apareciendo en Pipeline"). Auditoría de backend
  // confirmó que el DELETE de /crm/contacts/:id borra todo correctamente y
  // que Conversaciones.js y Contactos.js llaman exactamente el mismo
  // endpoint con el mismo id -- no hay bug de datos. La explicación que
  // sí encaja con "aunque recargo no se va": este tablero solo pide
  // contactos una vez al montar. Si quedó abierto en otra pestaña/ventana
  // del navegador mientras el contacto se borraba en otra, esa pestaña
  // nunca se entera hasta que alguien la refresca a mano. Repoblar al
  // volver a foco/visible cierra ese hueco sin depender de que Angelo
  // recuerde recargar.
  useEffect(() => {
    if (!hasAccess) return;
    const onFocus = () => loadContacts();
    const onVisibility = () => { if (document.visibilityState === 'visible') loadContacts(); };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [hasAccess, loadContacts]);

  useEffect(() => {
    if (!hasAccess || !selectedPipelineId) return;
    loadStagesAndTags(selectedPipelineId);
  }, [hasAccess, selectedPipelineId, loadStagesAndTags]);

  const createPipeline = async () => {
    if (!newPipelineName.trim()) return;
    try {
      const res = await apiClient.post('/api/pos/crm/pipelines', { name: newPipelineName.trim() });
      setNewPipelineName('');
      setAddingPipeline(false);
      if (showApprovalQueued(res)) return;
      await loadPipelines();
      if (res.data?.id) setSelectedPipelineId(res.data.id);
    } catch (err) {
      showAlert('error', 'Error', err.response?.data?.error || 'No se pudo crear el pipeline.');
    }
  };

  const saveRenamePipeline = async () => {
    if (!pipelineNameDraft.trim() || !editingPipelineId) { setEditingPipelineId(null); return; }
    try {
      const res = await apiClient.put(`/api/pos/crm/pipelines/${editingPipelineId}`, { name: pipelineNameDraft.trim() });
      setEditingPipelineId(null);
      if (showApprovalQueued(res)) return;
      loadPipelines();
    } catch (err) {
      showAlert('error', 'Error', 'No se pudo renombrar el pipeline.');
    }
  };

  const deletePipeline = (pipeline) => {
    setCustomAlert({
      type: 'confirm',
      title: 'Eliminar pipeline',
      message: `¿Eliminar el pipeline "${pipeline.name}" y todas sus etapas? Los contactos que estén en ellas quedan como "Sin etapa", no se borran.`,
      onConfirm: async () => {
        try {
          const res = await apiClient.delete(`/api/pos/crm/pipelines/${pipeline.id}`);
          if (showApprovalQueued(res)) return;
          if (selectedPipelineId === pipeline.id) setSelectedPipelineId(null);
          loadPipelines();
          loadContacts();
        } catch (err) {
          showAlert('error', 'Error', err.response?.data?.error || 'No se pudo eliminar el pipeline.');
        }
      }
    });
  };

  const moveContactToStage = async (contactId, stageId) => {
    const previous = contacts;
    const stage = stages.find(s => s.id === stageId) || null;
    setContacts(prev => prev.map(c => c.id === contactId
      // ⭐ Al mover de etapa, el contacto que NO tiene valor propio pasa a valer
      // lo que vale su etapa nueva. Sin recalcular acá, la tarjeta arrastrada
      // muestra el monto de la etapa anterior hasta que se recargue.
      ? {
          ...c,
          pipeline_stage_id: stageId,
          stage_name: stage?.name || null,
          stage_color: stage?.color || null,
          effective_value: c.opportunity_value != null
            ? Number(c.opportunity_value)
            : Number(stage?.default_value || 0)
        }
      : c));
    try {
      await apiClient.patch(`/api/pos/crm/contacts/${contactId}`, { pipeline_stage_id: stageId });
      setSelectedContact(prev => (prev && prev.id === contactId) ? { ...prev, pipeline_stage_id: stageId } : prev);
    } catch (err) {
      setContacts(previous);
      showAlert('error', 'Error', 'No se pudo mover el contacto de etapa.');
    }
  };

  const openContact = async (contact) => {
    setLoadingContact(true);
    setSelectedContact(contact);
    setNoteInput('');
    setShowTagPicker(false);
    setCreatingTag(false);
    try {
      const res = await apiClient.get(`/api/pos/crm/contacts/${contact.id}`);
      setSelectedContact(res.data);
    } catch (err) {
      showAlert('error', 'Error', 'No se pudo cargar el detalle del contacto.');
    } finally {
      setLoadingContact(false);
    }
  };

  const updateSelectedContact = async (patch) => {
    if (!selectedContact) return;
    try {
      await apiClient.patch(`/api/pos/crm/contacts/${selectedContact.id}`, patch);
      // ⭐ `effective_value` lo calcula el backend, así que al cambiar el valor
      // propio hay que recalcularlo acá también o la tarjeta del tablero sigue
      // mostrando el monto viejo hasta la próxima recarga.
      const withValue = (c) => {
        if (!Object.prototype.hasOwnProperty.call(patch, 'opportunity_value')) return { ...c, ...patch };
        const stage = stages.find(s => s.id === (patch.pipeline_stage_id ?? c.pipeline_stage_id));
        return {
          ...c,
          ...patch,
          effective_value: patch.opportunity_value ?? Number(stage?.default_value || 0)
        };
      };
      setSelectedContact(prev => withValue(prev));
      setContacts(prev => prev.map(c => c.id === selectedContact.id ? withValue(c) : c));
    } catch (err) {
      showAlert('error', 'Error', 'No se pudo actualizar el contacto.');
    }
  };

  const addTagToContact = async (tagId) => {
    if (!selectedContact) return;
    try {
      await apiClient.post(`/api/pos/crm/contacts/${selectedContact.id}/tags`, { tagId });
      const tag = tags.find(t => t.id === tagId);
      if (tag) setSelectedContact(prev => ({ ...prev, tags: [...(prev.tags || []), tag] }));
    } catch (err) {
      showAlert('error', 'Error', 'No se pudo asignar la etiqueta.');
    }
  };

  const createTag = async () => {
    const name = newTagName.trim();
    if (!name) return;
    try {
      await apiClient.post('/api/pos/crm/tags', { name, color: newTagColor });
      const tagsRes = await apiClient.get('/api/pos/crm/tags');
      const freshTags = tagsRes.data || [];
      setTags(freshTags);
      const created = freshTags.find(t => t.name === name) || freshTags[freshTags.length - 1];
      setNewTagName('');
      setNewTagColor(STAGE_COLOR_SWATCHES[0]);
      setCreatingTag(false);
      setShowTagPicker(false);
      if (created && selectedContact) await addTagToContact(created.id);
    } catch (err) {
      showAlert('error', 'Error', err.response?.data?.error || 'No se pudo crear la etiqueta.');
    }
  };

  const removeTagFromContact = async (tagId) => {
    if (!selectedContact) return;
    try {
      await apiClient.delete(`/api/pos/crm/contacts/${selectedContact.id}/tags/${tagId}`);
      setSelectedContact(prev => ({ ...prev, tags: (prev.tags || []).filter(t => t.id !== tagId) }));
    } catch (err) {
      showAlert('error', 'Error', 'No se pudo quitar la etiqueta.');
    }
  };

  const addNote = async () => {
    if (!noteInput.trim() || !selectedContact) return;
    try {
      await apiClient.post('/api/pos/crm/notes', { contactId: selectedContact.id, content: noteInput.trim() });
      setNoteInput('');
      const res = await apiClient.get(`/api/pos/crm/contacts/${selectedContact.id}`);
      setSelectedContact(res.data);
    } catch (err) {
      showAlert('error', 'Error', 'No se pudo guardar la nota.');
    }
  };

  const createStage = async ({ name, color }) => {
    if (!selectedPipelineId) return;
    try {
      const res = await apiClient.post('/api/pos/crm/pipeline-stages', { name, color, pipeline_id: selectedPipelineId });
      if (showApprovalQueued(res)) return;
      loadStagesAndTags(selectedPipelineId);
    } catch (err) {
      showAlert('error', 'Error', err.response?.data?.error || 'No se pudo crear la etapa.');
    }
  };

  const updateStage = async (stageId, patch) => {
    try {
      const res = await apiClient.put(`/api/pos/crm/pipeline-stages/${stageId}`, patch);
      if (showApprovalQueued(res)) return;
      loadStagesAndTags(selectedPipelineId);
    } catch (err) {
      showAlert('error', 'Error', 'No se pudo actualizar la etapa.');
    }
  };

  const reorderStage = async (stageId, direction) => {
    const index = stages.findIndex(s => s.id === stageId);
    const other = stages[index + direction];
    if (index < 0 || !other) return;
    try {
      await Promise.all([
        apiClient.put(`/api/pos/crm/pipeline-stages/${stageId}`, { order_index: other.order_index }),
        apiClient.put(`/api/pos/crm/pipeline-stages/${other.id}`, { order_index: stages[index].order_index }),
      ]);
      loadStagesAndTags(selectedPipelineId);
    } catch (_) { showAlert('error', 'Error', 'No se pudo reordenar la etapa.'); }
  };

  // Borrar una etapa no borra contactos -- el backend los deja en "Sin
  // etapa" (ver DELETE /api/pos/crm/pipeline-stages/:id).
  const deleteStage = (stage) => {
    setCustomAlert({
      type: 'confirm',
      title: 'Eliminar etapa',
      message: `¿Eliminar la etapa "${stage.name}"? Los contactos que estén en ella quedan como "Sin etapa", no se borran.`,
      onConfirm: async () => {
        try {
          const res = await apiClient.delete(`/api/pos/crm/pipeline-stages/${stage.id}`);
          if (showApprovalQueued(res)) return;
          loadStagesAndTags(selectedPipelineId);
          loadContacts();
        } catch (err) {
          showAlert('error', 'Error', 'No se pudo eliminar la etapa.');
        }
      }
    });
  };

  // ⭐ El tablero de un pipeline muestra: los contactos ya asignados a una
  // de SUS etapas, más los contactos sin ninguna etapa todavía (columna
  // "Sin etapa", compartida entre todos los pipelines -- no existe el
  // concepto de "sin etapa dentro de este pipeline puntual"). Así, un
  // contacto asignado a una etapa de OTRO pipeline no aparece acá, evitando
  // que dos tableros muestren la misma tarjeta como si conviviera en ambos.
  const stageIds = new Set(stages.map(s => s.id));
  const isMainPipeline = selectedPipelineId === pipelines[0]?.id;
  const visibleContacts = contacts.filter(c => stageIds.has(c.pipeline_stage_id) || (isMainPipeline && !c.pipeline_stage_id));

  if (loadingAccess) {
    return <div className="p-8 text-center text-gray-500">Cargando...</div>;
  }

  if (!hasAccess) {
    return (
      <div className="flex flex-col items-center justify-center h-full p-8 text-center">
        <Lock className="w-16 h-16 text-gray-300 mb-4" />
        <h2 className="text-xl font-bold text-gray-700 mb-2">Pipeline no está incluido en tu plan</h2>
        <p className="text-gray-500 max-w-md mb-4">
          Este módulo viene incluido desde el plan Premium, o se puede contratar aparte sobre tu plan actual. Contacta a soporte para activarlo.
        </p>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      {customAlert && (
        <CustomAlert
          type={customAlert.type}
          title={customAlert.title}
          message={customAlert.message}
          onClose={() => setCustomAlert(null)}
          onConfirm={customAlert.onConfirm}
          zIndex={80}
        />
      )}

      <div className="flex items-center px-4 py-2.5 border-b bg-white gap-4 overflow-x-auto">
        <h1 className="text-lg font-bold text-gray-800 flex items-center gap-2 flex-shrink-0">
          <Kanban className="w-5 h-5 text-blue-600" /> Pipeline
        </h1>
        {/* ⭐ Selector de pipelines (pedido de Angelo, 2026-08-05: "que
            pueda tener varios pipelines totalmente editables"). Un tab por
            pipeline, con renombrar/borrar inline -- versión básica del
            selector de canalizaciones de GHL. */}
        <div className="flex items-center gap-1.5 flex-shrink-0">
          {pipelines.map(p => (
            <div key={p.id} className="flex-shrink-0">
              {editingPipelineId === p.id ? (
                <input
                  autoFocus
                  value={pipelineNameDraft}
                  onChange={(e) => setPipelineNameDraft(e.target.value)}
                  onBlur={saveRenamePipeline}
                  onKeyDown={(e) => { if (e.key === 'Enter') saveRenamePipeline(); if (e.key === 'Escape') setEditingPipelineId(null); }}
                  className="px-2 py-1 border rounded-lg text-xs w-32"
                />
              ) : (
                <div
                  className={`group flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-semibold cursor-pointer ${selectedPipelineId === p.id ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                >
                  <span onClick={() => setSelectedPipelineId(p.id)}>{p.name}</span>
                  <button
                    onClick={() => { setEditingPipelineId(p.id); setPipelineNameDraft(p.name); }}
                    className={`opacity-0 group-hover:opacity-100 ${selectedPipelineId === p.id ? 'text-blue-100 hover:text-white' : 'text-gray-400 hover:text-gray-700'}`}
                    title="Renombrar"
                  >
                    <Pencil className="w-3 h-3" />
                  </button>
                  {pipelines.length > 1 && (
                    <button
                      onClick={() => deletePipeline(p)}
                      className={`opacity-0 group-hover:opacity-100 ${selectedPipelineId === p.id ? 'text-blue-100 hover:text-white' : 'text-gray-400 hover:text-red-600'}`}
                      title="Eliminar"
                    >
                      <Trash2 className="w-3 h-3" />
                    </button>
                  )}
                </div>
              )}
            </div>
          ))}
          {addingPipeline ? (
            <input
              autoFocus
              placeholder="Nombre del pipeline"
              value={newPipelineName}
              onChange={(e) => setNewPipelineName(e.target.value)}
              onBlur={() => { if (!newPipelineName.trim()) setAddingPipeline(false); }}
              onKeyDown={(e) => { if (e.key === 'Enter') createPipeline(); if (e.key === 'Escape') setAddingPipeline(false); }}
              className="px-2 py-1 border rounded-lg text-xs w-32"
            />
          ) : (
            <button
              onClick={() => setAddingPipeline(true)}
              className="flex items-center gap-1 px-2 py-1.5 rounded-lg text-xs font-semibold text-gray-400 hover:text-gray-600 border border-dashed"
              title="Nuevo pipeline"
            >
              <Plus className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      </div>

      <div className="flex-1 flex overflow-hidden">
        <PipelineBoard
          stages={stages}
          contacts={visibleContacts}
          loading={loading}
          draggedContactId={draggedContactId}
          setDraggedContactId={setDraggedContactId}
          onMoveContact={moveContactToStage}
          onOpenContact={openContact}
          onCreateStage={createStage}
          onUpdateStage={updateStage}
          onDeleteStage={deleteStage}
          onReorderStage={reorderStage}
        />

        {selectedContact && (
          <div className="w-80 flex-shrink-0 border-l bg-white overflow-y-auto p-4 space-y-5">
            <div className="flex items-start justify-between gap-2">
              <div>
                <h3 className="font-bold text-gray-800">{selectedContact.name || selectedContact.phone || 'Sin nombre'}</h3>
                <p className="text-sm text-gray-500">{selectedContact.phone}</p>
              </div>
              <button onClick={() => setSelectedContact(null)} className="text-gray-400 hover:text-gray-600 flex-shrink-0"><X className="w-4 h-4" /></button>
            </div>

            {onOpenConversations && (
              <button
                onClick={() => onOpenConversations(selectedContact)}
                className="w-full flex items-center justify-center gap-1.5 px-3 py-2 border border-blue-200 text-blue-700 bg-blue-50 rounded-lg text-xs font-semibold hover:bg-blue-100"
              >
                <MessageCircle className="w-3.5 h-3.5" /> Ver en Conversaciones
              </button>
            )}

            {loadingContact ? (
              <p className="text-xs text-gray-400">Cargando...</p>
            ) : (
              <>
                {selectedContact.pos_customer && (
                  <div className="border border-gray-200 rounded-lg p-3 bg-gray-50 space-y-1 text-xs text-gray-600">
                    <label className="text-xs font-bold text-gray-500 uppercase tracking-wide flex items-center gap-1 mb-1">
                      <IdCard className="w-3 h-3" /> Ficha del cliente (POS)
                    </label>
                    <p className="font-semibold text-gray-800">
                      {selectedContact.pos_customer.first_name} {selectedContact.pos_customer.last_name || ''}
                    </p>
                    {selectedContact.pos_customer.phone && (
                      <p className="flex items-center gap-1"><MessageCircle className="w-3 h-3 text-gray-400" /> {selectedContact.pos_customer.phone}</p>
                    )}
                    {selectedContact.pos_customer.sector && (
                      <p className="flex items-center gap-1"><MapPin className="w-3 h-3 text-gray-400" /> {selectedContact.pos_customer.sector}</p>
                    )}
                    {selectedContact.pos_customer.email && (
                      <p className="flex items-center gap-1"><Mail className="w-3 h-3 text-gray-400" /> {selectedContact.pos_customer.email}</p>
                    )}
                    <p className="text-[10px] text-gray-400 pt-1">Edición completa disponible desde Contactos o Conversaciones.</p>
                  </div>
                )}

                <div>
                  <label className="text-xs font-bold text-gray-500 uppercase tracking-wide">Etapa</label>
                  <select
                    value={selectedContact.pipeline_stage_id || ''}
                    onChange={(e) => {
                      const stageId = e.target.value ? Number(e.target.value) : null;
                      updateSelectedContact({ pipeline_stage_id: stageId });
                      setContacts(prev => prev.map(c => c.id === selectedContact.id ? { ...c, pipeline_stage_id: stageId } : c));
                    }}
                    className="w-full mt-1 px-2 py-1.5 border rounded-lg text-sm"
                  >
                    <option value="">Sin etapa</option>
                    {stages.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                  </select>
                </div>

                {/* ⭐ Valor de esta oportunidad (2026-08-08). Vacío = hereda el
                    valor de su etapa, que es el caso normal; se completa solo
                    cuando este trato en particular vale distinto. */}
                <div>
                  <label className="text-xs font-bold text-gray-500 uppercase tracking-wide">Valor de la oportunidad</label>
                  <div className="flex items-center gap-1.5 mt-1">
                    <span className="text-sm text-gray-500">$</span>
                    <input
                      type="number" min="0" step="0.01"
                      value={selectedContact.opportunity_value ?? ''}
                      placeholder={String(
                        stages.find(s => s.id === selectedContact.pipeline_stage_id)?.default_value ?? 0
                      )}
                      onChange={(e) => setSelectedContact(prev => ({
                        ...prev,
                        opportunity_value: e.target.value === '' ? null : Number(e.target.value)
                      }))}
                      onBlur={(e) => updateSelectedContact({
                        opportunity_value: e.target.value === '' ? null : Number(e.target.value)
                      })}
                      className="flex-1 px-2 py-1 border rounded-lg text-sm"
                    />
                  </div>
                  <p className="text-[10px] text-gray-400 mt-0.5">
                    {selectedContact.opportunity_value == null
                      ? 'Vacío: usa el valor de la etapa. Escribe un monto para que este contacto valga distinto.'
                      : 'Valor propio. Bórralo para volver a heredar el de la etapa.'}
                  </p>
                </div>

                <div>
                  <label className="text-xs font-bold text-gray-500 uppercase tracking-wide flex items-center gap-1"><Thermometer className="w-3 h-3" /> Temperatura</label>
                  <div className="flex gap-1.5 mt-1">
                    {TEMPERATURE_OPTIONS.map(opt => (
                      <button
                        key={opt.value}
                        onClick={() => updateSelectedContact({ temperature: opt.value })}
                        className={`flex-1 px-2 py-1 rounded-lg text-xs font-semibold border ${selectedContact.temperature === opt.value ? opt.color : 'bg-gray-50 text-gray-400 border-gray-200'}`}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="relative">
                  <label className="text-xs font-bold text-gray-500 uppercase tracking-wide flex items-center gap-1"><TagIcon className="w-3 h-3" /> Etiquetas</label>
                  <div className="flex flex-wrap gap-1.5 mt-1">
                    {(selectedContact.tags || []).length === 0 && (
                      <span className="text-xs text-gray-400 italic">Sin etiquetas</span>
                    )}
                    {(selectedContact.tags || []).map(t => (
                      <span key={t.id} className="flex items-center gap-1 px-2 py-0.5 rounded-full text-xs" style={{ backgroundColor: `${t.color}22`, color: t.color }}>
                        {t.name}
                        <button onClick={() => removeTagFromContact(t.id)}><X className="w-3 h-3" /></button>
                      </span>
                    ))}
                  </div>
                  <button
                    onClick={() => { setShowTagPicker(v => !v); setCreatingTag(false); }}
                    className="flex items-center gap-1 mt-1.5 px-2 py-1 rounded-lg text-xs border border-gray-300 text-gray-600 hover:border-gray-400"
                  >
                    <Plus className="w-3 h-3" /> Agregar etiqueta
                  </button>

                  {showTagPicker && (
                    <div className="absolute z-10 mt-1 w-56 bg-white border rounded-lg shadow-lg p-2">
                      <div className="max-h-40 overflow-y-auto space-y-0.5">
                        {tags.filter(t => !(selectedContact.tags || []).some(ct => ct.id === t.id)).map(t => (
                          <button
                            key={t.id}
                            onClick={() => { addTagToContact(t.id); setShowTagPicker(false); }}
                            className="w-full flex items-center gap-2 px-2 py-1.5 rounded-lg text-xs text-left hover:bg-gray-50"
                          >
                            <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: t.color }} />
                            {t.name}
                          </button>
                        ))}
                        {tags.filter(t => !(selectedContact.tags || []).some(ct => ct.id === t.id)).length === 0 && (
                          <p className="text-[10px] text-gray-400 px-2 py-1">No hay más etiquetas para agregar.</p>
                        )}
                      </div>
                      <div className="border-t mt-1.5 pt-1.5">
                        {creatingTag ? (
                          <div className="space-y-1.5">
                            <input
                              type="text"
                              autoFocus
                              placeholder="Nombre de la etiqueta..."
                              value={newTagName}
                              onChange={(e) => setNewTagName(e.target.value)}
                              onKeyDown={(e) => { if (e.key === 'Enter') createTag(); if (e.key === 'Escape') setCreatingTag(false); }}
                              className="w-full px-2 py-1.5 border rounded-lg text-xs"
                            />
                            <div className="flex gap-1">
                              {STAGE_COLOR_SWATCHES.map(c => (
                                <button key={c} onClick={() => setNewTagColor(c)} style={{ backgroundColor: c }} className={`w-4 h-4 rounded-full ${newTagColor === c ? 'ring-2 ring-offset-1 ring-gray-500' : ''}`} />
                              ))}
                            </div>
                            <div className="flex gap-1.5">
                              <button onClick={createTag} className="flex-1 text-[10px] py-1 bg-blue-600 text-white rounded font-semibold">Crear</button>
                              <button onClick={() => { setCreatingTag(false); setNewTagName(''); }} className="flex-1 text-[10px] py-1 bg-gray-200 text-gray-600 rounded font-semibold">Cancelar</button>
                            </div>
                          </div>
                        ) : (
                          <button
                            onClick={() => setCreatingTag(true)}
                            className="w-full flex items-center gap-1 px-2 py-1.5 rounded-lg text-xs text-blue-600 hover:bg-blue-50"
                          >
                            <Plus className="w-3 h-3" /> Nueva etiqueta
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                <div>
                  <label className="text-xs font-bold text-gray-500 uppercase tracking-wide">Notas internas</label>
                  <div className="mt-1 space-y-2 max-h-40 overflow-y-auto">
                    {(selectedContact.notes || []).map(n => (
                      <div key={n.id} className="bg-yellow-50 border border-yellow-200 rounded-lg p-2 text-xs text-gray-700">
                        {n.content}
                        <p className="text-[10px] text-gray-400 mt-1">{formatEcuadorDate(n.created_at)}</p>
                      </div>
                    ))}
                  </div>
                  <div className="flex gap-1.5 mt-2">
                    <input
                      type="text"
                      placeholder="Agregar nota..."
                      value={noteInput}
                      onChange={(e) => setNoteInput(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') addNote(); }}
                      className="flex-1 px-2 py-1.5 border rounded-lg text-xs"
                    />
                    <button onClick={addNote} className="px-2.5 py-1.5 bg-blue-600 text-white rounded-lg text-xs font-semibold hover:bg-blue-700">
                      <Plus className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default PipelineManager;
